import React, { memo } from 'react';

import { View } from 'react-native';

import Animated, {
    Extrapolation,
    interpolate,
    useAnimatedStyle,
    useDerivedValue,
} from 'react-native-reanimated';

import { lightGrey } from 'utils/colors';

import styles from './styles';
import { DotsType, PaginationDotsType } from './types';

const Dot = ({
    index,
    length,
    activeColor,
    size,
    spacing,
    isLast,
    ...props
}: DotsType) => {
    // On web galleryProgress is not updated unless it first called with destructuring assignment before useDerivedValue call
    const { galleryProgress } = props;

    const progress = useDerivedValue(() => {
        return galleryProgress.value > length - 1
            ? length - 1
            : galleryProgress.value;
    });

    const animatedStyle = useAnimatedStyle(() => {
        const opacity = interpolate(
            progress.value,
            [index - 1, index, index + 1],
            [0.5, 1, 0.5],
            {
                extrapolateLeft: Extrapolation.CLAMP,
                extrapolateRight: Extrapolation.CLAMP,
            },
        );

        return { backgroundColor: activeColor, opacity };
    });

    const style = {
        width: size,
        height: size,
        borderRadius: size / 2,
        marginRight: isLast ? 0 : spacing,
    };

    return <Animated.View style={[style, animatedStyle]} />;
};

const PaginationDots = ({
    length = 0,
    size = 10,
    spacing = 10,
    activeDotColor = lightGrey(),
    ...props
}: PaginationDotsType) => {
    // On web galleryProgress is not updated unless it first called with destructuring assignment
    const { galleryProgress } = props;

    return (
        <View style={styles.container}>
            {[...Array(length).keys()].map((_, i) => (
                <Dot
                    key={i.toString()}
                    index={i}
                    galleryProgress={galleryProgress}
                    size={size}
                    spacing={spacing}
                    activeColor={activeDotColor}
                    length={length}
                    isLast={i === length - 1}
                />
            ))}
        </View>
    );
};

export default memo(PaginationDots);
